import { Button } from '@thedealersconcierge/components';
import { t } from 'i18next';
import { FC, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Image } from 'scanbot-web-sdk/@types/core-types';
import Modal from '~/components/Modal';
import Scanner, {
  applyContrast,
  applyCorrectRotation
} from '~/components/Scanner';
import { getScanbotSdk } from '~/lib/scanbot-client';

/**
 * ## Driver License Scanner Modal
 *
 * Notes:
 * - This changes behaviour based on wether or not it is on Kiosk mode, this is due to Ipad need a different aspect ratio so it can capture much close on Kiosk Stand
 *
 */
export const DriverLicenseScannerModal: FC<{
  isOpen: boolean;
  onDone: (file: File) => void;
  onClose: () => void;
  title: string;
}> = ({ isOpen, onDone, title, onClose }) => {
  const [capturedFile, setCapturedFile] = useState<
    Uint8Array<ArrayBufferLike> | undefined | null
  >();

  const displayText = useMemo(
    () => ({
      hint: {
        OK: t(
          'Capturing your driver license... Please do not move the camera.'
        ),
        OK_SmallSize: t('The driver license is too small. Try moving closer.'),
        OK_BadAngles: t('This is a bad angle. Hold the device straight.'),
        OK_BadAspectRatio: t(
          'Rotate the device so the driver license fits better.'
        ),
        OK_OffCenter: t(
          'Try holding the device at the center of the document.'
        ),
        Error_NothingDetected: t(
          'Please hold the device over a driver license.'
        ),
        Error_Brightness: t('It is too dark. Try turning on a light.'),
        Error_Noise: t('Move the license to a clear surface.')
      }
    }),
    [t]
  );

  /**
   * Handles post processing after we capture the image
   * Will apply:
   * 1. Correct document rotation
   * 2. Contrast filter
   */
  const handleCapture = async (rawImage: Image) => {
    const sdk = await getScanbotSdk();

    const rotated = await applyCorrectRotation(sdk, rawImage);
    const contrastAdjusted = await applyContrast(sdk, rotated);

    const result = await sdk.imageToJpeg(contrastAdjusted);

    setCapturedFile(result);
  };

  const MemoizedScanner = useMemo(() => {
    return (
      <Scanner
        text={displayText}
        /**
         * Optimize the aspect ratio for phones
         */
        aspectRatio={16 / 10}
        onNewPage={handleCapture}
        enableManualCapture={true}
      />
    );
  }, [displayText]);

  // Build the thumbnail from capture license
  const thumbnailBlob = capturedFile
    ? new Blob([capturedFile], { type: 'image/jpeg' })
    : null;
  const thumbnailUrl = thumbnailBlob
    ? URL.createObjectURL(thumbnailBlob)
    : undefined;

  return (
    <Modal
      isOpen={isOpen}
      className="flex flex-col w-dvw h-dvh max-h-dvh !max-w-full rounded-none"
    >
      <div className="flex flex-col h-dvh">
        <div className="flex-shrink-0">
          <div className="relative flex flex-row justify-center items-center w-full py-spacing-04">
            <div className="absolute left-2">
              <Button
                onClick={() => {
                  // Close modal and reset scanned license
                  onClose();
                  setCapturedFile(null);
                }}
                variant="GHOST"
                size="SMALL"
                label={t('Close')}
              />
            </div>

            <h3
              className="capitalize text-center"
              data-test-id={'desktop-webcam-modal'}
            >
              {title}
            </h3>
          </div>
        </div>

        <div className="flex flex-col flex-grow min-h-0 relative">
          {thumbnailUrl ? (
            <div className="flex flex-col size-full space-y-spacing-8 items-center justify-center px-spacing-08">
              <div className="max-h-[70%] max-w-[70%]">
                <img
                  src={thumbnailUrl}
                  alt="Driver License"
                  className="max-h-full max-w-full object-contain rounded-md"
                />
              </div>

              <div className="flex items-center justify-center w-full mt-spacing-08 gap-spacing-08">
                <Button
                  label={t('Retake')}
                  variant="SECONDARY"
                  onClick={() => {
                    // Reset the scanned state to restart scanning
                    setCapturedFile(null);
                  }}
                />

                <Button
                  label={t('Accept and continue')}
                  onClick={() => {
                    // This is very not likely to happen; it is a safeguard
                    if (!capturedFile) {
                      toast.error(
                        t('Scan failed, retry scanning or contact admin')
                      );
                      return;
                    }

                    const imageFile = new File(
                      [capturedFile],
                      'scanned-license.jpeg',
                      { type: 'image/jpeg' }
                    );

                    onDone(imageFile);
                    setCapturedFile(null);
                  }}
                  dataTestId="scanner-capture-accept"
                />
              </div>
            </div>
          ) : (
            <div className="w-full h-full relative max-h-dvh">
              {MemoizedScanner}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
